import { SamplePlayback } from "./sample-playback.ts";

function clock(seconds: number): string {
  const whole = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

export function initSamplePlayer(): void {
  const card = document.querySelector<HTMLElement>("[data-sample-player]");
  if (!card) return;
  const tracks = new Map(Array.from(card.querySelectorAll<HTMLAudioElement>("[data-sample-audio]"),
    audio => [audio.dataset.sampleAudio!, audio]));
  const controls = card.querySelector<HTMLElement>(".sample-controls");
  const fallback = card.querySelector<HTMLElement>(".sample-audio");
  const play = card.querySelector<HTMLButtonElement>(".sample-play");
  const icon = play?.querySelector("i");
  const seek = card.querySelector<HTMLInputElement>(".sample-seek");
  const time = card.querySelector<HTMLOutputElement>(".sample-time");
  const progressBar = card.querySelector<HTMLElement>(".sample-progress span");
  const feedback = card.querySelector<HTMLElement>(".sample-feedback");
  const buttons = card.querySelectorAll<HTMLButtonElement>("[data-sample-version]");
  const select = card.querySelector<HTMLSelectElement>("[data-sample-select]");
  if (!tracks.has("original") || !tracks.has("processed") || !controls || !fallback || !play || !icon || !seek || !time || !progressBar || !feedback) return;

  let context: AudioContext;
  try {
    context = new AudioContext({ latencyHint: "interactive" });
  } catch {
    return; // Keep the native audio controls if Web Audio is unavailable.
  }
  let player: SamplePlayback<string> | null = null;
  let selected = "processed";
  let wantsPlayback = false;
  let playVersion = 0;
  let animationFrame = 0;

  function renderProgress(): void {
    const duration = player?.duration ?? 0;
    const position = player?.position ?? 0;
    const progress = duration ? Math.min(100, position / duration * 100) : 0;
    seek!.disabled = !duration;
    seek!.value = String(Math.round(progress * 10));
    seek!.setAttribute("aria-valuetext", `${clock(position)} of ${clock(duration)}`);
    time!.textContent = `${clock(position)} / ${duration ? clock(duration) : "–:––"}`;
    progressBar!.style.width = `${progress}%`;
  }

  function render(): void {
    play!.setAttribute("aria-label", `${wantsPlayback ? "Pause" : "Play"} ${tracks.get(selected)!.dataset.sampleLabel} sample`);
    icon!.className = `ph-bold ${wantsPlayback ? "ph-pause" : "ph-play"}`;
    play!.setAttribute("aria-busy", String(wantsPlayback && !player));
    renderProgress();
    for (const button of buttons) button.setAttribute("aria-pressed", String(button.dataset.sampleVersion === selected));
    if (select) select.value = selected;
  }

  function animateProgress(): void {
    animationFrame = 0;
    renderProgress();
    if (player?.playing) animationFrame = window.requestAnimationFrame(animateProgress);
  }

  function pause(): void {
    ++playVersion;
    wantsPlayback = false;
    player?.pause();
    window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    render();
  }

  // Decode every version up front so preset changes share the same audio clock.
  const ready = Promise.all(Array.from(tracks, async ([version, audio]) => {
    const response = await fetch(audio.currentSrc || audio.src);
    if (!response.ok) throw new Error("Sample unavailable");
    return [version, await context.decodeAudioData(await response.arrayBuffer())] as const;
  })).then((buffers) => {
    player = new SamplePlayback(context, Object.fromEntries(buffers),
      Object.fromEntries(Array.from(tracks, ([version, audio]) => [version, Number(audio.dataset.sampleSpeed) || 1])),
      card.hasAttribute("data-sample-restart"));
    player.switchTo(selected);
    player.onended = pause;
    render();
  });

  void ready.catch(() => {
    pause();
    controls.hidden = true;
    fallback.hidden = false;
    void context.close();
  });

  async function start(): Promise<void> {
    const version = ++playVersion;
    wantsPlayback = true;
    feedback!.hidden = true;
    // Resume in the click handler to satisfy mobile browsers' user-gesture policy.
    const resumed = context.resume();
    render();
    try {
      await Promise.all([ready, resumed]);
      if (version !== playVersion || !wantsPlayback) return;
      for (const audio of document.querySelectorAll<HTMLAudioElement>("audio")) audio.pause();
      player!.play();
      render();
      if (!animationFrame) animationFrame = window.requestAnimationFrame(animateProgress);
    } catch {
      if (version !== playVersion) return;
      pause();
      feedback!.textContent = "This sample could not be played. Try again, or choose an audio file below.";
      feedback!.hidden = false;
    }
  }

  play.addEventListener("click", () => {
    if (wantsPlayback) pause();
    else void start();
  });

  function selectVersion(next: string | undefined): void {
    if (!next || !tracks.has(next)) return;
    selected = next;
    player?.switchTo(next);
    render();
  }

  for (const button of buttons) {
    button.addEventListener("click", () => selectVersion(button.dataset.sampleVersion));
  }
  select?.addEventListener("change", () => selectVersion(select.value));

  seek.addEventListener("input", () => {
    if (!player) return;
    player.seek(Number(seek.value) / 1000 * player.duration);
    render();
  });

  // Buffered demos and native uploaded/result previews must remain exclusive.
  document.addEventListener("play", (event) => {
    if (!(event.target instanceof HTMLMediaElement)) return;
    pause();
    for (const audio of document.querySelectorAll<HTMLAudioElement>("audio")) {
      if (audio !== event.target) audio.pause();
    }
  }, true);
  window.addEventListener("pagehide", () => {
    pause();
    for (const audio of tracks.values()) audio.pause();
  });
  window.addEventListener("audio-recording-start", pause);
  render();
  fallback.hidden = true;
  controls.hidden = false;
}
